import { useEffect } from 'react'

import { type ResolvedTheme, resolveTheme, type ThemePreference, useThemeStore } from '@/lib/store/useThemeStore'
import { log } from '@/lib/utils/log'

const THEME_META_COLORS: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#111113'
}

function applyTheme(theme: ResolvedTheme) {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_META_COLORS[theme])
}

function applyAndLogTheme(preference: ThemePreference) {
  const resolvedTheme = resolveTheme(preference)
  applyTheme(resolvedTheme)
  log('Theme applied', 'theme', { preference, resolvedTheme })
}

/**
 * Applies the resolved theme to the document and keeps it in sync with the
 * OS preference while in `system` mode. Mount once at the root of the app.
 */
export function useTheme() {
  const preference = useThemeStore((state) => state.preference)

  useEffect(() => {
    applyAndLogTheme(preference)

    if (preference !== 'system') return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyAndLogTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])
}
